/**
 * IrisLoadingOverlay - Loading screen for Iris AI recommendation processing
 * Adapted from PRISM's AIProgressOverlay for Foundations (no MUI dependency)
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';

const WARMUP_MESSAGES = [
  'Just a moment, warming up my neural pathways...',
  'Grabbing my coffee... one sec.',
  'Booting up — give me just a moment.',
  'Shaking off sleep mode... almost ready.',
  'Spinning up the engines. Hang tight.',
  "Let me get my glasses on — I'll need them for this.",
  'Stretching my tensors... be right with you.',
];

const WORKING_MESSAGES = [
  'Analyzing your Big Six risk categories...',
  'Cross-referencing 1,220 recommendations against your profile...',
  'Scoring candidates with the RAG Rubric formula...',
  'Balancing your recommendation portfolio...',
  'Contextualizing recommendations for your organization...',
  'Evaluating maturity gaps across all categories...',
  'Matching quick wins to your highest-risk areas...',
  "Making sure I don't miss anything important.",
  'Double-checking the portfolio balance. Thoroughness matters.',
  'Your personalized roadmap is coming into focus...',
];

const PATIENCE_MESSAGES = [
  "Still here, still working. This is a big one.",
  "Taking my time to get this right for you.",
  "Complex analysis in progress — quality over speed.",
  "I haven't forgotten about you. Almost done.",
  "The thorough approach pays off. Bear with me.",
];

function pickRandom(arr, exclude) {
  const filtered = exclude ? arr.filter(m => m !== exclude) : arr;
  return filtered[Math.floor(Math.random() * filtered.length)];
}

const IrisLoadingOverlay = ({ visible = false }) => {
  const [elapsed, setElapsed] = useState(0);
  const [message, setMessage] = useState('');
  const [fadeIn, setFadeIn] = useState(true);
  const intervalRef = useRef(null);
  const messageRef = useRef(null);
  const elapsedRef = useRef(0);

  const initialMessage = useMemo(
    () => (visible ? pickRandom(WARMUP_MESSAGES) : ''),
    [visible]
  );

  useEffect(() => {
    if (visible) {
      setElapsed(0);
      elapsedRef.current = 0;
      setMessage(initialMessage);
      setFadeIn(true);

      intervalRef.current = setInterval(() => {
        setElapsed(prev => {
          elapsedRef.current = prev + 1;
          return prev + 1;
        });
      }, 1000);

      messageRef.current = setInterval(() => {
        setFadeIn(false);
        setTimeout(() => {
          const seconds = elapsedRef.current;
          let pool;
          if (seconds < 8) pool = WARMUP_MESSAGES;
          else if (seconds < 60) pool = WORKING_MESSAGES;
          else pool = PATIENCE_MESSAGES;

          setMessage(prev => pickRandom(pool, prev));
          setFadeIn(true);
        }, 350);
      }, 5000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (messageRef.current) clearInterval(messageRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (messageRef.current) clearInterval(messageRef.current);
    };
  }, [visible, initialMessage]);

  if (!visible) return null;

  const formatElapsed = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div role="status" aria-live="polite" aria-busy="true" style={styles.container}>
      <div style={styles.card}>
        {/* Iris icon */}
        <div style={styles.iconCircle}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00d9ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v2c0 4.4 3.6 8 8 8s8-3.6 8-8v-2a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4Z"/>
            <circle cx="9" cy="13" r="1"/>
            <circle cx="15" cy="13" r="1"/>
            <path d="M10 17c.5.3 1.2.5 2 .5s1.5-.2 2-.5"/>
          </svg>
        </div>

        <h3 style={styles.title}>Iris</h3>
        <p style={styles.subtitle}>AI-Powered Recommendation Engine</p>

        {/* Pulsing dots */}
        <div style={styles.dotsContainer}>
          {[0, 1, 2].map(i => (
            <div
              key={i}
              style={{
                ...styles.dot,
                animationDelay: `${i * 0.16}s`,
              }}
            />
          ))}
        </div>

        {/* Timer */}
        <div style={styles.timer}>{formatElapsed(elapsed)}</div>

        {/* Rotating message */}
        <div style={styles.messageContainer}>
          <p
            style={{
              ...styles.message,
              opacity: fadeIn ? 1 : 0,
              transition: 'opacity 0.35s ease',
            }}
          >
            {message}
          </p>
        </div>
      </div>

      {/* CSS animation for pulsing dots */}
      <style>{`
        @keyframes irisPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 16px',
  },
  card: {
    textAlign: 'center',
    backgroundColor: 'var(--ra-deep-blue)',
    border: '1px solid var(--ra-glass-border)',
    borderRadius: '16px',
    padding: '40px 48px',
    maxWidth: '440px',
    width: '100%',
    boxShadow: 'var(--ra-glass-shadow)',
    backdropFilter: 'blur(12px)',
  },
  iconCircle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 217, 255, 0.08)',
    border: '2px solid rgba(0, 217, 255, 0.25)',
    marginBottom: '16px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: 'var(--ra-text-primary)',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--ra-text-secondary)',
    margin: '0 0 20px 0',
    letterSpacing: '0.02em',
  },
  dotsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    margin: '20px 0',
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: 'var(--ra-cyber-aqua)',
    animation: 'irisPulse 1.4s infinite ease-in-out',
  },
  timer: {
    fontSize: '28px',
    fontWeight: '600',
    fontFamily: 'monospace',
    color: '#ccd6f6',
    margin: '8px 0',
  },
  messageContainer: {
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
    padding: '0 8px',
  },
  message: {
    fontSize: '14px',
    color: 'var(--ra-text-secondary)',
    fontStyle: 'italic',
    margin: 0,
    lineHeight: '1.5',
  },
};

export default IrisLoadingOverlay;
